import is from 'is';
import snakecaseKeys from 'snakecase-keys';
import { NotImplementedError, ValueError } from '@utils/errors';
import { clamp } from '@utils/numberUtils';
import { makeQueryContract } from '../helpers';

// Helper: escape string content for GraphQL value quoting
function escapeGqlString(s) {
  return String(s).replace(/\\/g, '\\\\');
}

// Format a JS value for inclusion in a GraphQL field value.
function formatGraphQLValue(v) {
  if (typeof v === 'boolean' || typeof v === 'number') return String(v);
  return `"${escapeGqlString(String(v))}"`;
}

function throwNotImplemented() {
  throw new NotImplementedError();
}

/**
 * @classdesc An "abstract" class containing inheritable methods which should be overridden by usable child classes.
 * @abstract
 * @class
 */
class PropertiesReturner {
  /**
   * A list of properties that may be returned for a query.
   *
   * @property
   * @returns {string[]}
   */
  get acceptedReturnProperties() {
    return [];
  }
  /**
   * Returns a carriage-return delimited string with a list of properties to request from the GraphQL
   * request (e.g.: Query, Mutation, etc). This string will nominally only contain valid properties
   * having been filtered through the acceptedReturnProperties property.
   *
   * @returns {string}
   */
  returnSyntax() {
    return '';
  }
}

// Helpers used by CriteriaCreator switch cases (module-private)

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** { key: value, key2: { innerKey: innerValue } } → "{ key: value, key2: { innerKey: innerValue } }" */
function serializeObjectLiteral(obj) {
  const entries = Object.entries(obj)
    .map(([key, value]) => `${key}: ${formatGraphQLValue(value)}`)
    .join(', ');
  return `{ ${entries} }`;
}

/** Handles or-nested-in-and and nested object values. */
function serializeGroupObject(group, knownTypes = {}) {
  const parts = Object.entries(group)
    .map(([key, value]) => {
      if (key === 'or' && Array.isArray(value) && value.length > 0) {
        return `or: [${value.map((item) => serializeGroupObject(snakecaseKeys(item), knownTypes)).join(', ')}]`;
      }
      if (isPlainObject(value)) {
        return `${key}: ${serializeObjectLiteral(value)}`;
      }
      const fieldType = knownTypes[key]?.type;
      if (fieldType === 'enum' || fieldType === 'number') {
        return `${key}: ${value}`;
      }
      return `${key}: ${formatGraphQLValue(value)}`;
    })
    .join(', ');
  return `{ ${parts} }`;
}

/** Serializes an array of objects into GraphQL array syntax using serializeGroupObject. */
function serializeItemArray(items, knownTypes = {}) {
  return `[${items.map((item) => serializeGroupObject(snakecaseKeys(item), knownTypes)).join(', ')}]`;
}

/**
 * Filters the given properties against acceptedReturnProperties, stores
 * the valid ones in the returnProperties store, and returns `this` for chaining.
 *
 * Used identically in QueryCreator, SingleQueryCreator, MutationCreator,
 * and SingleRecordMutationCreator.
 *
 * @param {string[]} properties
 * @param {string[]} acceptedReturnProperties
 * @param {object} returnPropertiesStore
 * @returns {boolean} Whether any properties were stored.
 */
function storeReturnProperties(properties, acceptedReturnProperties, returnPropertiesStore) {
  const filtered = properties.filter((property) => acceptedReturnProperties.includes(property));
  if (filtered.length === 0) {
    return false;
  }
  for (const property of filtered) {
    returnPropertiesStore[property] = true;
  }
  return true;
}

/**
 * A class for generating criteria syntax for a GraphQL query.
 *
 * @abstract
 * @classdesc
 * @class
 */
export class CriteriaCreator {
  #criteria = {};

  constructor(criteria = {}) {
    if (!is.object(criteria)) {
      return;
    }
    const filtered = {};
    for (const [key, value] of Object.entries(criteria)) {
      if (!Object.hasOwn(this.acceptedCriteria, key)) {
        continue;
      }
      const { type, triplequoted } = this.acceptedCriteria[key];
      switch (type) {
        case 'string':
          filtered[key] = value === null
            ? null
            : triplequoted
              ? `"""${value}"""`
              : `"${escapeGqlString(value)}"`;
          break;
        case 'boolean':
          filtered[key] = value === null
            ? null
            : Boolean(value).valueOf().toString();
          break;
        case 'multi-string':
          filtered[key] = value === null
            ? null
            : `"""${value}"""`;
          break;
        case 'object':
          filtered[key] = value === null
            ? null
            : `${JSON.stringify(value)}`;
          break;
        case 'or':
          if (Array.isArray(value) && value.length > 0) {
            filtered[key] = serializeItemArray(value, this.acceptedCriteria);
          }
          break;
        case 'not':
          if (isPlainObject(value)) {
            filtered[key] = serializeObjectLiteral(snakecaseKeys(value));
          }
          break;
        case 'metadata_filter':
          if (isPlainObject(value)) {
            const filterKey = value.key;
            const filterValue = value.value;
            if (filterKey !== undefined && filterValue !== undefined) {
              filtered[key] = `{ key: ${formatGraphQLValue(filterKey)}, value: ${formatGraphQLValue(String(filterValue))} }`;
            }
          }
          break;
        case 'and':
          if (Array.isArray(value) && value.length > 0) {
            filtered[key] = serializeItemArray(value, this.acceptedCriteria);
          }
          break;
        case 'enum':
        case 'number':
          filtered[key] = value;
          break;
      }
    }
    this.#criteria = snakecaseKeys(filtered);
  }

  get criteria() {
    return this.#criteria;
  }

  /**
   * Getter.
   * Provides an array of objects which detail the acceptable criteria properties as well as their format.
   * The object properties are:
   * - 'name': the name of the criteria property.
   * - 'format': the format of the property (e.g.: string, boolean, etc). Defaults to 'string'.
   * - 'quoted': the format is 'string' the value will be quoted if true. Defaults to true.
   */
  get acceptedCriteria() {
    return [];
  }

  /**
   * Indicates if the criteria request is valid.
   *
   * A request is considered valid if the criteria private property has keys.
   *
   * @property
   * @returns {boolean}
   */
  get isValid() {
    return !is.empty(this.#criteria);
  }

  toString() {
    if (!this.isValid) {
      return '';
    }
    return `criteria: { ${Object.entries(this.#criteria).map(([key, value]) => `${key}: ${value}`).join('\n')} }`;
  }
}

/**
 * A class for generating the pagination syntax for a GraphQL Query.
 */
export class PaginationCreator {
  constructor(options = {}) {
    let {
      startOnPage,
      countPerPage,
      returns,
    } = options || {};
    if (is.number(startOnPage)) {
      startOnPage = clamp(startOnPage, { minimum: 1 });
    }
    if (is.number(countPerPage)) {
      countPerPage = clamp(countPerPage, { minimum: 1 });
    }
    this.settings = {
      startOnPage,
      countPerPage,
      returns,
    };
  }

  /**
   * Getter.
   * Indicates if the pagination request is valid.
   *
   * A pagination request is valid if either, or both, startOnPage or countPerPage is a number.
   *
   * @property
   * @returns {boolean}
   */
  get isValid() {
    const { startOnPage, countPerPage } = this.settings;
    if (is.number(startOnPage)) {
      return true;
    }
    return is.number(countPerPage);
  }

  /**
   * @override
   */
  get acceptedReturnProperties() {
    return [
      'total_pages',
      'total_records',
      'count_per_page',
      'current_page',
    ];
  }

  /**
   * Returns the pagination syntax to place in the query arguments.
   * If no pagination query information is provided (e.g.: startOnPage, countPerPage) this method returns an
   * empty string.
   * Also, if no return properties are provided (e.g.: totalPages, countPerPage, totalRecords, currentPage)
   * then an empty string will also be returned.
   *
   * Otherwise, this method will return a string formatted like so: pagination: { //pagination query }.
   * @example
   * // returns 'pagination: { count_per_page: 1, start_on_page: 10 }'
   * new PaginationCreator({ startOnPage: 1, countPerPage: 10, returns: ['totalPages'] }).querySyntax()
   * @example
   * // returns ''
   * new PaginationCreator({}).querySyntax()
   * new PaginationCreator({ startOnPage: 1, returns: [] })
   * @returns {string}
   */
  querySyntax() {
    const converted = snakecaseKeys(this.settings);
    const { start_on_page, count_per_page } = converted;
    if (!(is.number(start_on_page) || is.number(count_per_page))) {
      return '';
    }
    const options = [];
    if (is.number(start_on_page)) {
      options.push(`start_on_page: ${start_on_page}`);
    }
    if (is.number(count_per_page)) {
      options.push(`count_per_page: ${count_per_page}`);
    }
    return `pagination: { ${options.join(', ')} }`;
  }

  /**
   * Provides the pagination property to be returned.
   *
   * If no pagination query information is provided (e.g.: startOnPage, countPerPage) this method returns an
   * empty string.
   * Also, if no return properties are provided (e.g.: totalPages, countPerPage, totalRecords, currentPage)
   * then an empty string will also be returned.
   *
   * Otherwise, the method will return the requested properties, followed by a carriage-return (i.e.: \n),
   * contained in the syntax 'pagination {}'.
   * @example
   * // returns 'pagination {\n total_pages\n }\n'
   * new PaginationCreator({ startOnPage: 1, countPerPage: 10, returns: ['totalPages'] }).querySyntax()
   * @example
   * // returns ''
   * new PaginationCreator({}).querySyntax()
   * new PaginationCreator({ startOnPage: 1, returns: [] })
   * @returns {string}
   */
  returnSyntax() {
    if (!this.isValid) {
      return '';
    }
    const { returns = {} } = this.settings;
    const requested = Object.keys(snakecaseKeys(returns));
    let valid = !is.empty(requested)
      ? requested.filter((item) => this.acceptedReturnProperties.includes(item))
      : [];
    if (is.empty(valid)) {
      valid = this.acceptedReturnProperties;
    }
    return `pagination {\n${valid.join('\n')}\n}\n`;
  }
}

/**
 * A class for generating the sort syntax for a GraphQL Query.
 */
export class SortCreator {
  constructor(options = {}) {
    this.field = options.field || null;
    this.direction = options.direction || null;
  }

  get isValid() {
    return this.field !== null;
  }

  /**
   * Returns the sort syntax to place in the query arguments.
   * @param {boolean} [arrayFormat=false] When true, wraps the sort object in an array.
   * @returns {string}
   */
  querySyntax(arrayFormat = false) {
    if (!this.isValid) {
      return '';
    }
    const parts = [`field: "${this.field}"`];
    if (this.direction) {
      parts.push(`direction: ${this.direction.toUpperCase()}`);
    }
    const inner = `{ ${parts.join(', ')} }`;
    return arrayFormat ? `sort:[${inner}]` : `sort: ${inner}`;
  }
}


/**
 * Base Query creation class.
 *
 * A class geared towards creating requests for multiple records. Contains methods for providing
 * criteria for narrowing the response as well as server-side pagination methods.
 *
 * @classdesc
 * @class
 */
export class QueryCreator extends PropertiesReturner {
  #name;
  #id;
  #pagination = {};
  #sort = {};
  #sortArrayFormat = false;
  #criteria = {};
  #criteriaClass = null;
  #returnProperties = {};

  /**
   * Creates an instance of QueryCreator.
   *
   * @param {string} name The name of the query (i.e.: streams).
   * @param {CriteriaCreator} [criteriaClass = CriteriaCreator] The criteria creator class to use.
   * @throws {TypeError} If the name argument is not a string or is empty.
   * @returns {void}
   */
  constructor(name, criteriaClass = CriteriaCreator) {
    super();
    if (!is.string(name)) {
      throw new TypeError('name must be a string.');
    }
    if (is.empty(name)) {
      throw new ValueError('name cannot be empty');
    }
    this.#name = name;
    this.#criteriaClass = criteriaClass;
  }
  /**
   * Getter.
   *
   * Provides access to the name property value.
   * @property
   * @readonly
   */
  get name() {
    return this.#name;
  }

  get criteria() {
    return this.#criteriaClass ? new this.#criteriaClass(this.#criteria).toString() : '';
  }

  /**
   * Getter.
   * Returns a deep-copy of the pagination object.
   *
   * This prevents accidental modification of the pagination object.
   * @property
   * @returns {object}
   */
  get pagination() {
    if (typeof structuredClone === 'undefined') {
      global.structuredClone = (obj) => JSON.parse(JSON.stringify(obj));
    }
    return structuredClone(this.#pagination);
  }

  /**
 *
 * @param {string | number} id The identifier to search for.
 * @chainable
 */
  setId(id) {
    // check type of id is either a number or a string that is not empty
    if (is.number(id) || (is.string(id) && !is.empty(id))) {
      this.#id = id;
      return this;
    }
    return this;
  }
  /**
   * Adds a pagination key.
   *
   * If the key provided is not either startOnPage or countPerPage, the execution is ignored.
   *
   * If the value is not a number, the execution is ignored.
   *
   * If the value is less-than 1 it will be raised to 1.
   *
   * @param {string} key One of either startOnPage or countPerPage.
   * @param {*} value The number to assign to either key.
   * @chainable
   */
  addPagination(key, value) {
    if (!['startOnPage', 'countPerPage'].includes(key)) {
      return this;
    }

    if (!is.number(value)) {
      return this;
    }

    this.#pagination[key] = clamp(value, { minimum: 1 });
    return this;
  }
  /**
   * Sets the sort field and direction for the query.
   *
   * @param {string} field The field name to sort by.
   * @param {string} direction The sort direction ('asc' or 'desc').
   * @chainable
   */
  setSort(field, direction = 'asc') {
    if (is.string(field) && !is.empty(field)) {
      this.#sort = { field, direction };
    }
    return this;
  }

  /**
   * Opts this query into array-format sort syntax: sort:[{field: "...", direction: ASC}]
   * Required by endpoints whose schema defines sort as a list (e.g. streams).
   * @chainable
   */
  useSortArrayFormat() {
    this.#sortArrayFormat = true;
    return this;
  }
  /**
   * Adds a criteria to the query.
   *
   * If the query key is not a valid one, it will be ignored.
   *
   * @param {string} key The criteria key to add.
   * @param {string | boolean | number} value The value to associate with the key.
   * @chainable
   */
  addCriteria(key, value) {
    if (!is.string(key)) {
      return this;
    }

    if (is.empty(key)) {
      return this;
    }

    if (is.undefined(value)) {
      return this;
    }

    this.#criteria[key] = value;
    return this;
  }

  /**
   * Setter.
   * Sets the criteria object on the class.
   *
   * @chainable
   */
  setCriteria(criteria) {
    if (!is.object(criteria)) {
      return this;
    }

    this.#criteria = criteria;
    return this;
  }

  /**
   * Used to limit the properties returned for a pagination request.
   *
   * If this method is not used then all of the available properties will be requested during the query request.
   *
   * If the key passed in is not a string, or is an empty string, the method will exit gracefully.
   *
   * @param {...string} keys One of total_pages, total_records, count_per_page, or current_page. Other keys can be used
   * but they will be ignored during toString() rendering.
   * @chainable
   */
  addPaginationReturn(...keys) {
    const { returns = {} } = this.#pagination;
    for (const key of keys) {
      if (!is.string(key)) {
        continue;
      }
      if (is.empty(key)) {
        continue;
      }
      returns[key] = true;
    }
    return this;
  }

  /**
   * Adds the properties to the data request of the query.
   *
   * The properties are filtered through the acceptedReturnProperties array. Any requested
   * properties that are not in this list are ignored.
   *
   * @param  {...string} properties The properties to return in the data portion of the query.
   * @chainable
   */
  addReturnProperties(...properties) {
    storeReturnProperties(properties, this.acceptedReturnProperties, this.#returnProperties);
    return this;
  }
  /**
   * Provides the data section of the query.
   *
   * If no properties have been requested, then this will return an empty string.
   *
   * @returns {string}
   */
  returnSyntax() {
    if (is.empty(Object.keys(this.#returnProperties))) {
      return '';
    }
    const properties = Object.keys(snakecaseKeys(this.#returnProperties));
    return `data {\n${properties.join('\n')}\n}\n`;
  }
  /**
   * A string formatted for use as a GraphQL query request.
   *
   * @returns {string}
   */
  /**
   * Override in subclasses to inject additional query arguments into the contract.
   * @returns {string[]}
   */
  extraArgs() {
    return [];
  }

  toString() {
    const defaults = ['success', 'error'];
    const pagination = new PaginationCreator(this.pagination);
    const sort = new SortCreator(this.#sort);
    const contract = makeQueryContract(
      this.criteria,
      pagination.querySyntax(),
      sort.querySyntax(this.#sortArrayFormat),
      ...this.extraArgs()
    );
    defaults.push(pagination.returnSyntax());
    defaults.push(this.returnSyntax());
    if (this.#id !== undefined) {
      return `{\n${this.name}(id: "${this.#id}") {\n${defaults.join('\n')}}\n}`;
    }
    return `{\n${this.name}${contract} {\n${defaults.join('\n')}}\n}`;
  }
}

export class SingleQueryCreator extends QueryCreator {
  #id = null;
  #returnProperties = {};

  constructor(name, criteriaClass = CriteriaCreator) {
    super(name, criteriaClass);
  }

  /**
   * @param {string | number} id The identifier to search for.
   * @chainable
   */
  setId(id) {
    if (!is.number(id) && !is.string(id)) {
      return this;
    }
    this.#id = id;
  }

  /**
   * Adds the properties to the data request of the query.
   *
   * The properties are filtered through the acceptedReturnProperties array. Any requested
   * properties that are not in this list are ignored.
   *
   * @param  {...string} properties The properties to return in the data portion of the query.
   * @chainable
   */
  addReturnProperties(...properties) {
    storeReturnProperties(properties, this.acceptedReturnProperties, this.#returnProperties);
    return this;
  }

  addPagination = throwNotImplemented;
  addPaginationReturn = throwNotImplemented;
  addCriteria = throwNotImplemented;

  toString() {
    const defaults = ['success', 'error'];
    // If no return properties were added, use default 'id' and 'name'
    const returnProperties = Object.keys(this.#returnProperties).length > 0
      ? Object.keys(this.#returnProperties)
      : ['id', 'name'];
    // Merge defaults with return properties
    const fieldsToReturn = [
      ...defaults,
      'data {',
      ...returnProperties,
      '}',
    ];
    return `{
      ${this.name}(id: "${this.#id}") {
        ${fieldsToReturn.join('\n      ')}
      }
    }`;
  }
}

export class MutationCreator extends QueryCreator {
  #returnProperties = {};

  constructor(name, criteriaClass = CriteriaCreator) {
    super(name, criteriaClass);
  }

  /**
   * Adds the properties to the data request of the mutation.
   *
   * The properties are filtered through the acceptedReturnProperties array. Any requested
   * properties that are not in this list are ignored.
   *
   * @param  {...string} properties The properties to return in the data portion of the mutation.
   * @chainable
   */
  addReturnProperties(...properties) {
    storeReturnProperties(properties, this.acceptedReturnProperties, this.#returnProperties);
    return this;
  }

  addPagination = throwNotImplemented;
  addPaginationReturn = throwNotImplemented;

  toString() {
    const defaults = [
      'success',
      'error',
      'tracking_id',
    ];
    const returnProperties = Object.keys(this.#returnProperties).length > 0
      ? Object.keys(this.#returnProperties)
      : ['id', 'name'];
    const fieldsToReturn = [
      ...defaults,
      'data {',
      ...returnProperties,
      this.returnSyntax(),
      '}',
    ];
    const input = this.criteria.replace(/^criteria/, 'input');
    return `mutation {${this.name} (
      ${input}) {
       ${fieldsToReturn.join('\n      ')}\n
    }\n }`;
  }
}

export class SingleRecordMutationCreator extends QueryCreator {
  #id = null;
  #returnProperties = {};

  constructor(name, criteriaClass = CriteriaCreator) {
    super(name, criteriaClass);
  }

  get criteria() {
    return super.criteria;
  }

  /**
   * @param {string | number} id The identifier to update.
   * @chainable
   */
  setId(id) {
    if (!is.number(id) && !is.string(id)) {
      return this;
    }
    this.#id = id;
  }

  /**
   * Adds the properties to the data request of the mutation.
   *
   * The properties are filtered through the acceptedReturnProperties array. Any requested
   * properties that are not in this list are ignored.
   *
   * @param  {...string} properties The properties to return in the data portion of the mutation.
   * @chainable
   */
  addReturnProperties(...properties) {
    storeReturnProperties(properties, this.acceptedReturnProperties, this.#returnProperties);
    return this;
  }

  addPagination = throwNotImplemented;
  addPaginationReturn = throwNotImplemented;

  toString() {
    const defaults = [
      'success',
      'error',
      'tracking_id',
    ];
    const returnProperties = Object.keys(this.#returnProperties).length > 0
      ? Object.keys(this.#returnProperties)
      : ['id', 'name'];
    const fieldsToReturn = [
      ...defaults,
      'data {',
      ...returnProperties,
      this.returnSyntax(),
      '}',
    ];
    const input = this.criteria.replace(/^criteria/, 'input');
    return `mutation {${this.name} (
      id: "${this.#id}"\n
      ${input}) {
       ${fieldsToReturn.join('\n      ')}
    }\n }`;
  }
}
